/**
 * Where a user should land after logging in, and the small cache that lets the
 * rest of the app reuse that answer.
 *
 * The decision is made by the server (`GET /api/auth/profile-status` returns a
 * `landing` block): it knows whether the user has any *connected* page and which
 * page they last selected, validated against reality. Deciding it client-side
 * from localStorage is what used to send returning users to a page they no
 * longer had.
 *
 * `ProtectedRoute` already calls profile-status on every protected mount, but it
 * renders *above* `FacebookPageProvider`, so it publishes the answer here rather
 * than through React context.
 */

import { API_ENDPOINTS } from "./api";

/**
 * localStorage cache of the current page selection. Owned here rather than by
 * FacebookPageContext so imports flow one way (context -> landing); the context
 * needs `whenLandingReady`, and having landing import the key back from it
 * would form a module cycle.
 *
 * The cache only hydrates UI chrome synchronously on load. The server decides
 * where a user actually lands.
 */
export const SELECTED_PAGE_STORAGE_KEY = "selected_facebook_page";

export interface LandingPage {
  id: string;
  name: string;
  platform?: "facebook" | "instagram";
  username?: string | null;
  picture?: string | null;
}

export interface LandingInfo {
  hasConnectedPage: boolean;
  pageId: string | null;
  page: LandingPage | null;
  path: string;
  rememberedValid: boolean;
}

let resolved: LandingInfo | null = null;
let settled = false;
let waiters: Array<(value: LandingInfo | null) => void> = [];

/** Publish the answer (called by ProtectedRoute and the login entry points). */
export const setLanding = (info: LandingInfo | null): void => {
  resolved = info;
  settled = true;
  waiters.forEach((resolve) => resolve(info));
  waiters = [];
};

export const getLanding = (): LandingInfo | null => resolved;

/** Clear on logout / impersonation switch so one user's answer can't leak into another's session. */
export const resetLanding = (): void => {
  resolved = null;
  settled = false;
  waiters = [];
};

/**
 * Resolves as soon as profile-status returns; `null` on failure or timeout so
 * callers never hang waiting for an answer that isn't coming.
 */
export const whenLandingReady = (
  timeoutMs = 4000
): Promise<LandingInfo | null> => {
  if (settled) return Promise.resolve(resolved);
  return new Promise((resolve) => {
    waiters.push(resolve);
    setTimeout(() => resolve(resolved), timeoutMs);
  });
};

/** `/accounts` is always the safe answer: it's where a user with no page belongs. */
export const landingPathFrom = (info?: LandingInfo | null): string =>
  info?.path || (info?.pageId ? `/accounts/${info.pageId}/comments` : "/accounts");

interface ApiLike {
  get: (endpoint: string, config?: any) => Promise<{ data: any }>;
}

/**
 * The single post-login decision, shared by `/callback` and the
 * already-authenticated branch of `/login` so the two can never drift apart.
 *
 * Also seeds the localStorage cache the page context hydrates from, so the
 * top-bar selector shows the right page on first paint instead of flashing
 * "Select account". That cache only pre-fills UI chrome — the destination
 * itself always comes from the server.
 */
export const resolveLandingAndSeed = async (
  api: ApiLike
): Promise<{ target: string; requiresSignup: boolean }> => {
  try {
    const { data } = await api.get(API_ENDPOINTS.AUTH_PROFILE_STATUS);
    const landing: LandingInfo | null = data?.landing ?? null;
    setLanding(landing);

    if (landing?.page) {
      localStorage.setItem(
        SELECTED_PAGE_STORAGE_KEY,
        JSON.stringify(landing.page)
      );
    } else {
      localStorage.removeItem(SELECTED_PAGE_STORAGE_KEY);
    }

    return {
      target: landingPathFrom(landing),
      requiresSignup: !!data?.requiresSignup,
    };
  } catch (error) {
    console.error(
      "🔴 [LANDING] Could not resolve landing path, defaulting to /accounts",
      error
    );
    setLanding(null);
    // Deliberately do NOT fall back to the localStorage page here — trusting a
    // stale cached id is the bug this replaces. /accounts is always valid.
    localStorage.removeItem(SELECTED_PAGE_STORAGE_KEY);
    return { target: "/accounts", requiresSignup: false };
  }
};
